import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from "react-intl";

//Components
import { IconButton, Tooltip, Checkbox, SearchInput } from "@gpstrackit/gtc-react-controls";
import { Timezone } from '../../../../../../../components/uiControls';
import { NotificationManager } from 'Components/Notifications';
import MapObject from 'Components/Map/providers/mapObject';
import { Grid, Typography, Tooltip as MuiTooltip } from '@mui/material';
import { MenuComponent } from 'Components/Menu';
import TableView from 'Components/TableView';

//Helpers
import { copyToClipboard } from 'Core/data/Helpers';
import { endpoints } from 'Core/defaultValues';

//Enums
import { TypeLayer } from '../../../../../../../components/Map/providers/enums';

//Actions
import { catalogLoad, drawGetDirections, addPointers, setMapRoutesOptions } from 'Redux/actions';
import { etaCreateEditSetField } from '../etaCreateEdit/redux/slice';
import { etaContactsOpenModal } from '../etaContacts/redux/slice';
import { etaDeleteOpenModal } from '../etaDelete/redux/slice';

//Styles
import './styles.scss';

import { createSelector } from 'reselect'

import { useDataGridThrottler } from 'Core/hooks'
import { unitSelectors } from 'Core/realtime/redux/unitSlice';
import { lastReadingSelectors } from 'Core/realtime/redux/lastReadingSlice';
import { getUnitsNaturalOrder } from 'Core/data/Helpers';

const getUnitsCatalog = createSelector([state => unitSelectors.selectAll(state)], getUnitsNaturalOrder);
const getReadingsCatalog = (state) => lastReadingSelectors.selectEntities(state);

const getVehicleTags = (state) => state.realtimeMapUtilitiesVehiclesListRedux.tagsVehicleList;

/*********************************************************
 * Get information from pagination elements
*********************************************************/
const getData = createSelector(
    [getUnitsCatalog, getReadingsCatalog, getVehicleTags],
    (unitsList, readings, vehicleTags) => {
        let result = []
        unitsList?.forEach((unit) => {
            try {
                if (unit.id == "-1" || (!unit.id && !unit.CUnits_id)) return
                const idUnit = unit.id

                let lastReading = readings?.[idUnit]
                const lat = lastReading?.latitude || unit?.latitude || unit?.lat
                const lng = lastReading?.longitude || unit?.longitude || unit?.lng
                const label = unit?.label || lastReading?.deviceLabel;
                if (!Array.isArray(unit?.tags) || unit.tags.length === 0) {
                    unit = { ...unit, tags: [{ color: "000000", hasChildren: false, id: -1, label: "Untagged", parentId: null, path: "" }] }
                }

                //filter unit for existing tag
                unit = {
                    ...unit,
                    tags: !vehicleTags?.length ? unit.tags : unit.tags?.filter(tag => vehicleTags?.some(vehicleTag => tag?.id == -1 || vehicleTag?.id === tag?.id))
                }

                // if the unit no have tags or none is the group "Untagged", it is omitted
                if (!unit?.tags?.length && !unit?.tags?.some(tag => tag.id === -1)) return; // No is entered on the list of vehicle because no have permisions

                if (!lastReading?.haveLatLng) return; // No is entered on the list of vehicle because no have lat and lng

                const item = {
                    id: unit.id || lastReading?.deviceId,
                    label: label,
                    lat: lat ? parseFloat(lat) : 0,
                    lng: lng ? parseFloat(lng) : 0,
                    latitude: lat ? parseFloat(lat) : 0,
                    longitude: lng ? parseFloat(lng) : 0,
                    haveLatLng: lastReading?.haveLatLng, // this vehicle no have lat and lng, cant show on map
                    address: lastReading?.address,
                    fulladdress: lastReading?.address?.formatted || '',
                    changeLabel: (unit?.label != label),
                    changeLatLng: lastReading?.changeLatLng,
                    driverName: unit?.driverName,
                    lastReading: lastReading,
                    tags: unit.tags || []
                }
                result.push(item);
            } catch (e) {
                console.log('e', e)
                NotificationManager.error('Error', `Error src/components/Map/components/FromToMap/index.js to create show list of vehicles - ${e}`, 3000, null, null, 'error');
            }
        });
        return result
    }
)

const EtaTable = (props) => {
    const { mapRef, selectedRows, setSelectedRows, searchText, onSearch, onChangePage, expiredMode, page, setPage, showLink } = props;

    const dispatch = useDispatch();
    const { messages } = useIntl();
    const tableRef = useRef();

    const routeProperties = {
        optimizeWaypoints: true,
        avoidTolls: false,
        avoidHighways: false,
    }

    /*********************************************************
     * use compute data to sync values showing in table
    *********************************************************/
    const computedData = useSelector(state => getData(state) || []);
    const propertiesToValidate = useMemo(() => ["label", "lat", "lng", "changeLatLng", "haveLatLng", "tags"], []);

    // Hook handles both Throttled Arrays for `<Map/>` and imperative delta diffing for markers via `handleMapUpdates`
    const units = useDataGridThrottler(
        computedData,
        1000,
        () => { },
        "id",
        propertiesToValidate
    );

    //Redux States
    const loadingUnits = useSelector(state => state.catalogsRedux.loadingUnits);
    const resultDelete = useSelector((state) => state.etaDeleteRedux.result);
    const openModal = useSelector(state => state.realtimeMapEtaLinkCreateEditRedux.open);
    const { result, loading, total } = useSelector(state => state.realtimeMapEtaLinkRedux);
    const { user: { dateformat, brandDomains } } = useSelector((state) => state.securityRedux);
    const locale = useSelector(state => state.commomRedux.language);
    const { pointers } = useSelector((state) => state.realtimeMapRedux);

    //Local States
    const [itemSelected, setItemSelected] = useState(0);

    const generateSvgIcon = () => {
        let svg = `
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="red"><path d="M0 0h24v24H0z" fill="none"/><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
        `
        let svgIcon = {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
            scaledSize: new google.maps.Size(32, 32)
        };
        return svgIcon
    }

    const getInfoBubble = (address) => {
        return (
            <div style={{ minWidth: 270, maxWidth: 270, minHeight: 20, marginLeft: 8, paddingTop: 5 }}>
                <span>{address}</span>
            </div>
        )
    };

    const buildMarker = (id, position) => {
        mapRef.current.removeLayer(TypeLayer.EtaLinkMarker);
        setTimeout(() => {
            const currentPosition = { lat: position.latitude, lng: position.longitude };
            const svgIcon = generateSvgIcon()
            const marker = new MapObject().buildMarker({
                id: id,
                position: currentPosition,
                layerName: TypeLayer.EtaLinkMarker,
                heading: 0,
                color: "green",
                eventName: 'no event',
                draggable: false,
                icon: svgIcon,
                showLabel: true,
                onClick: (newMarker) => {
                    mapRef?.current?.showMarkerModal(newMarker, null, getInfoBubble(position?.address), currentPosition, 10);
                }
            })

            mapRef?.current?.addMapObject(marker);
            mapRef?.current?.centerByFitBounds([position], 0);
            mapRef.current?.updateModalPosition({ position: currentPosition })
        }, 1000)

    }

    const createRouteOnView = (startPoint, endPoint) => {
        if (expiredMode) {
            buildMarker("marker-1", endPoint)
        } else {
            mapRef?.current?.deleteRouteInMap();
            dispatch(drawGetDirections(false));
            dispatch(addPointers([]));

            const tempPointers = [
                {
                    id: '1',
                    lat: parseFloat(startPoint?.latitude || 0),
                    lng: parseFloat(startPoint?.longitude || 0),
                    label: "Unit",
                    heading: 0,
                    color: '000',
                    eventName: 'no event',
                    draggable: false,
                    updatePointers: true,
                    hideMarker: true
                },
                {
                    id: '2',
                    lat: parseFloat(endPoint?.latitude || 0),
                    lng: parseFloat(endPoint?.longitude || 0),
                    label: messages['eta_destination'],
                    heading: 0,
                    color: '000',
                    eventName: 'no event',
                    draggable: false,
                    updatePointers: true,
                    hideMarker: true,

                }
            ];

            dispatch(drawGetDirections(false))
            //save pointers and shows into map
            dispatch(addPointers([...tempPointers]));
            dispatch(setMapRoutesOptions(routeProperties));
        }
    }

    const getValuesChecked = (id) => {
        const checkedElements = [...selectedRows]
        const findElement = checkedElements.find((item) => item.id == id)
        if (findElement) {
            return findElement.checked
        }
        return false
    }

    const onSelectCard = (row) => {
        if (!loadingUnits) {
            setItemSelected(row?.id)
            let currentUnit = units.find((item) => item.id === row?.unitId)
            createRouteOnView({ latitude: currentUnit?.latitude, longitude: currentUnit?.longitude }, { latitude: row?.latitude, longitude: row?.longitude, address: row?.address })
        }
    }

    const addCheckedElement = (id, name, value, row) => {
        if (!loadingUnits) {
            const checkedElements = [...selectedRows]
            const findElement = checkedElements.find((item) => item.id == id)
            if (findElement) {
                checkedElements.forEach(element => {
                    if (element.id == id) {
                        element.checked = value
                        element.current = row
                    }
                });
            } else {
                checkedElements.push({ id: id, name: name, checked: value, current: row });
            }
            setSelectedRows([...checkedElements]);
        }
    }

    const handlerList = (array) => {
        const object = {};
        const newArray = [];
        for (let i = 0; i < array.length; i++) {
            if (!object[array[i].id]) {
                object[array[i].id] = true;
                newArray.push(array[i]);
            }
        }
        return newArray
    }

    useEffect(() => {
        if (pointers && pointers.length > 1) {
            mapRef?.current?.deleteRouteInMap()
            dispatch(drawGetDirections(true))
        }
    }, [pointers]);

    useEffect(() => {
        tableRef?.current?.setData([])
    }, [])

    useEffect(() => {
        if (openModal || resultDelete) {
            mapRef?.current?.removeLayer(TypeLayer.EtaLinkMarker);
            mapRef?.current?.deleteRouteInMap();
            dispatch(drawGetDirections(false));
            dispatch(addPointers([]));
            setItemSelected(0)
        }
    }, [openModal, resultDelete])

    return (
        <div style={{ paddingTop: 10 }}>
            <div style={{ marginBottom: 10 }}>
                <SearchInput
                    searchText={searchText}
                    onSearchText={onSearch}
                    placeholder={messages['search']}
                />
            </div>
            <TableView
                ref={tableRef}
                data={handlerList(result)}
                loading={loading}
                page={page}
                rowsPerPage={25}
                name="routes-list"
                topSpaceSize={380}
                infiniteScroll={true}
                viewType="list"
                messages={messages}
                itemSelector={false}
                serverSide
                onChangePage={(newPage) => {
                    onChangePage(newPage);
                    setPage(newPage);
                }}
                total={total}
                moreRowsAvailable={(handlerList(result)?.length || 0) < total}
                renderRow={(row) => {
                    return (
                        <div style={{ padding: '0px 2px 7px 2px' }}>
                            <div className={row?.id == itemSelected ? "eta-card eta-container-selected" : "eta-card eta-container"}>
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                >
                                    <Grid item>
                                        <Checkbox
                                            id={'checked_' + row?.id}
                                            checked={getValuesChecked(row?.id)}
                                            value={getValuesChecked(row?.id)}
                                            className={'eta-checkbox'}
                                            onChange={(checked) => addCheckedElement(row?.id, row?.title, checked, row)}
                                        />
                                    </Grid>
                                    <Grid item xs zeroMinWidth style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography
                                            noWrap
                                            variant="subtitle1"
                                            className="eta-title"
                                            onClick={() => onSelectCard(row)}
                                        >
                                            {row?.unit?.label}
                                        </Typography>
                                        {
                                            <MuiTooltip title={messages['eta_contacts']} placement="bottom">
                                                <span>
                                                    <IconButton
                                                        variant="secondary"
                                                        icon="people"
                                                        size="small"
                                                        disabled={loading}
                                                        onClick={() => dispatch(etaContactsOpenModal({ open: true, data: row }))}
                                                    />
                                                </span>
                                            </MuiTooltip>
                                        }
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    wrap="nowrap"
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    onClick={() => onSelectCard(row)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <Grid item>
                                        {messages['eta_destination']}:
                                    </Grid>
                                    <Grid item xs zeroMinWidth style={{ marginLeft: 5 }}>
                                        <Tooltip title={row?.address}>
                                            <Typography noWrap variant="subtitle2" color={row?.id == itemSelected ? 'inherit' : 'textSecondary'}>
                                                {row?.address || ""}
                                            </Typography>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                                {
                                    expiredMode &&
                                    <Grid
                                        container
                                        wrap="nowrap"
                                        direction="row"
                                        justifyContent="flex-start"
                                        alignItems="center"
                                        onClick={() => onSelectCard(row)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <Grid item>
                                            {messages['eta_type_status']}:
                                        </Grid>
                                        <Grid item xs zeroMinWidth style={{ marginLeft: 5 }}>
                                            <Typography noWrap variant="subtitle2" color={row?.id == itemSelected ? 'inherit' : 'textSecondary'}>
                                                {messages[`eta_${row?.status}`] || row?.status}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                }
                                <Grid
                                    container
                                    wrap="nowrap"
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    onClick={() => onSelectCard(row)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <Grid item>
                                        {messages['eta_start']}:
                                    </Grid>
                                    <Grid item xs zeroMinWidth style={{ marginLeft: 5 }}>
                                        {
                                            row?.startDate &&
                                            <Timezone
                                                date={new Date(row?.startDate)}
                                                dateFormat={dateformat}
                                                timeFormat="hh:mm a"
                                                timezone={row?.unit?.timeZone?.name || "UTC"}
                                                className="vicinity-tool-date"
                                                customFormat={true}
                                                locale={locale}
                                                noWrap={false}
                                                oneControl={true}
                                            />
                                        }
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    wrap="nowrap"
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    onClick={() => onSelectCard(row)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <Grid item>
                                        {messages['eta_end']}:
                                    </Grid>
                                    <Grid item xs zeroMinWidth style={{ marginLeft: 5 }}>
                                        <Timezone
                                            date={new Date(row?.expirationDate)}
                                            dateFormat={dateformat}
                                            timeFormat="hh:mm a"
                                            timezone={row?.unit?.timeZone?.name || "UTC"}
                                            className="vicinity-tool-date"
                                            customFormat={true}
                                            locale={locale}
                                            noWrap={false}
                                            oneControl={true}
                                        />
                                    </Grid>
                                </Grid>
                                {
                                    showLink &&
                                    <Grid
                                        container
                                        wrap="nowrap"
                                        direction="row"
                                        justifyContent="flex-start"
                                        alignItems="center"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <Grid item>
                                            <a className="link-gtc-rc" onClick={() => {
                                                let url = `${endpoints?.CLOUD_URL}/etaLink/${row?.code}`
                                                if (brandDomains && process.env.STACK === "production") {
                                                    try {
                                                        let domains = JSON.parse(brandDomains)
                                                        if (domains.length) {
                                                            url = `https://${domains[0]}/etaLink/${row?.code}`
                                                        }
                                                    } catch (error) { }
                                                }
                                                copyToClipboard(url);
                                            }}>{messages['eta_copyLink']}</a>
                                        </Grid>
                                    </Grid>
                                }
                                <MenuComponent
                                    key={`menu-${row?.id}`}
                                    isDisable={loading}
                                    options={expiredMode ? [messages['delete']] : [messages['edit'], messages['delete']]}
                                    icon={'more_vert'}
                                    className="eta-actions showInHover"
                                    onClick={(type) => {
                                        if (type === messages['edit']) {
                                            dispatch(etaCreateEditSetField({ open: true, eta: row }))
                                        } else if (type === messages['delete']) {
                                            dispatch(etaDeleteOpenModal([row]))
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    )
                }}
            />
        </div>
    )
}

export default EtaTable